import React from "react";

import { CardExpandSheet } from "./CardExpandSheet";

// Title + caption with a TikTok/Reels-style read-more. Captions can run long
// (up to the 500-char cap) and the player can't scroll (it would swallow the
// swipe), so the collapsed view line-clamps the title/caption and a "… more"
// opens the full text in the shared CardExpandSheet — the same pattern the
// image+text card uses. The media pauses while the sheet is open and resumes on
// close (only if it was playing). Renders inside both the full-bleed overlay
// (white text over the video) and the split / embed layout (dark text on
// white), switched by `onWhite`.
//
// Media control is abstracted behind `mediaController` so this works for both
// the native <video> card and the YouTube/Vimeo embed card:
//   mediaController = { isPlaying(): bool, pause(): void, play(): void }
// Relative size steps (mirrors the image+text card). Medium is the existing
// baseline so cards without an explicit size render exactly as before. Caption
// sizes are context-specific so "medium" matches the prior hardcoded value in
// each layout: text-base on the white split panel, text-sm in the video overlay.
const TITLE_SIZE_CLASS = { small: "text-xl", medium: "text-2xl", large: "text-3xl" };
const CAPTION_SIZE_CLASS_WHITE = { small: "text-sm", medium: "text-base", large: "text-lg" };
const CAPTION_SIZE_CLASS_OVERLAY = { small: "text-xs", medium: "text-sm", large: "text-base" };

export function VideoTextBlock({
  title,
  caption,
  onWhite,
  mediaController,
  isActive,
  onSheetOpenChange,
  // Typography options (parity with the image+text card). Omitted == defaults.
  emoji = null,
  titleSize = "medium",
  captionSize = "medium",
  titleBold = true,
  titleItalic = false,
}) {
  const titleSizeClass = TITLE_SIZE_CLASS[titleSize] || TITLE_SIZE_CLASS.medium;
  const captionSizeMap = onWhite
    ? CAPTION_SIZE_CLASS_WHITE
    : CAPTION_SIZE_CLASS_OVERLAY;
  const captionSizeClass = captionSizeMap[captionSize] || captionSizeMap.medium;
  const titleWeightClass = titleBold === false ? "font-normal" : "font-bold";
  const titleItalicClass = titleItalic === true ? "italic" : "";
  const titleRef = React.useRef(null);
  const captionRef = React.useRef(null);
  const sheetScrollRef = React.useRef(null);
  const wasPlayingRef = React.useRef(false);
  const [clamped, setClamped] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);

  const measure = React.useCallback(() => {
    const over = (el) => !!el && el.scrollHeight - el.clientHeight > 4;
    setClamped(over(titleRef.current) || over(captionRef.current));
  }, []);
  React.useLayoutEffect(() => {
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    if (titleRef.current) ro.observe(titleRef.current);
    if (captionRef.current) ro.observe(captionRef.current);
    return () => ro.disconnect();
  }, [measure, title, caption]);

  // Collapse the sheet when this card is no longer active (swiped away).
  React.useEffect(() => {
    if (!isActive) setExpanded(false);
  }, [isActive]);

  // Focus the sheet's scroll region on open so Arrow keys scroll it (the deck
  // suppresses its own hotkeys while a data-deck-sheet is open).
  React.useEffect(() => {
    if (expanded && sheetScrollRef.current) {
      sheetScrollRef.current.focus({ preventScroll: true });
    }
  }, [expanded]);

  const open = (e) => {
    e.stopPropagation();
    e.preventDefault();
    wasPlayingRef.current = mediaController ? mediaController.isPlaying() : false;
    // Flag the sheet as open BEFORE pausing so any readiness retry already
    // in flight is suppressed too (the card reads this via sheetOpenRef).
    onSheetOpenChange && onSheetOpenChange(true);
    mediaController && mediaController.pause();
    setExpanded(true);
  };
  const close = () => {
    setExpanded(false);
    onSheetOpenChange && onSheetOpenChange(false);
    if (mediaController && isActive && wasPlayingRef.current) {
      mediaController.play();
    }
  };

  return (
    <>
      {emoji && (
        <div className="text-4xl mb-2 leading-none">{emoji}</div>
      )}
      {title && (
        <h2
          ref={titleRef}
          className={`${
            onWhite ? "text-gray-900 mb-3" : "mb-2"
          } ${titleSizeClass} ${titleWeightClass} ${titleItalicClass} leading-tight break-words line-clamp-2`}
        >
          {title}
        </h2>
      )}
      {caption && (
        <p
          ref={captionRef}
          className={`${
            onWhite ? "text-gray-700" : "opacity-90"
          } ${captionSizeClass} leading-relaxed break-words line-clamp-3`}
        >
          {caption}
        </p>
      )}
      {clamped && (
        // pointer-events-auto so it's tappable even inside the full-bleed
        // overlay (which is pointer-events-none so taps reach the video for
        // play/pause). Stop pointer down/up from bubbling to the root's
        // videoTapHandlers (same guards as the mute button) — otherwise a tap
        // toggles play/pause before open() runs, so wasPlayingRef records the
        // post-toggle state and closing the sheet leaves the video paused.
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          onClick={open}
          style={{ pointerEvents: "auto" }}
          className={`mt-1 text-sm font-semibold ${
            onWhite ? "text-violet-700" : "text-white"
          }`}
        >
          … more
        </button>
      )}
      <CardExpandSheet
        open={expanded}
        onClose={close}
        title={title}
        scrollRef={sheetScrollRef}
      >
        {caption}
      </CardExpandSheet>
    </>
  );
}
